import { Grid } from "@mui/material";
import { Process } from "../../models/caster.models";
import { CustomButton } from "./Buttons";
import { StatusCodes } from "./constants";

interface ArriveButtonsProps {
  process: Process;
  disableButtons: boolean;
  setDisableButtons: (value: boolean) => void;
  changeHeatStatus: (heatId: number, newStatus: string) => Promise<void>;
  refreshData: () => Promise<void>;
  loadData: (data: any) => void;
  pause: () => void;
  setShowArrive: (show: boolean) => void;
  setIsLadleOpen: (open: boolean) => void;
  setIsLadleClose: (close: boolean) => void;
  setErrorMessage: (message: string) => void;
  saveProcess: ({
    processId,
    field,
    value,
  }: {
    processId: number;
    field: string;
    value: boolean | string | number | Date | null;
  }) => Promise<void>;
  saveProcessData: ({
    processId,
    field,
    value,
  }: {
    processId: number;
    field: string;
    value: boolean | string | number | Date | null;
  }) => Promise<void>;
}

interface ButtonDefinition {
  key: string;
  label: (process: Process) => string;
  isDisabled: (process: Process, disableButtons: boolean) => boolean;
  onClick: (props: ArriveButtonsProps) => Promise<void>;
  errorMessage: string;
}

export const ArriveButtons = (props: ArriveButtonsProps): JSX.Element => {
  const { process, disableButtons, setDisableButtons, setErrorMessage } = props;

  const handleButtonAction = async (
    action: () => Promise<void>,
    errorMessage: string,
  ) => {
    setDisableButtons(true);
    try {
      await action();
    } catch (error) {
      setErrorMessage(errorMessage);
    } finally {
      setDisableButtons(false);
    }
  };

  const buttons: ButtonDefinition[] = [
    {
      key: "arrive",
      label: (process) => (process.End ? "Arrive" : "Undo Arrive"),
      isDisabled: (process, disableButtons) => {
        if (disableButtons || process.IsOldProcess) {
          return true;
        }

        // For unlinked processes (HeatId is null)
        if (process.HeatId === null) {
          if (process.End === null) {
            return true;
          }
          return false;
        }

        const canArrive = process.End !== null;
        const canUndoArrive =
          process.Start !== null && process.LadleOpen === null;

        return !(canArrive || canUndoArrive);
      },
      onClick: async ({ process, changeHeatStatus, pause, setShowArrive }) => {
        if (process.HeatStatusCode === StatusCodes.ARRIVE) {
          await changeHeatStatus(process.HeatId, StatusCodes.READYTOCAST);
        } else {
          pause();
          setShowArrive(true);
        }
      },
      errorMessage: "Error in Arrive operation",
    },
    {
      key: "open",
      label: (process) => (process.LadleOpen ? "Undo Open" : "Open"),
      isDisabled: (process, disableButtons) => {
        if (disableButtons || process.IsOldProcess) {
          return true;
        }

        if (process.LadleClose !== null) {
          return true;
        }

        const canOpen = process.Start !== null && process.LadleOpen === null;
        const canUndoOpen =
          process.Start !== null && process.LadleOpen !== null;

        return !(canOpen || canUndoOpen);
      },
      onClick: async ({
        process,
        changeHeatStatus,
        refreshData,
        saveProcessData,
      }) => {
        if (process.HeatId !== null) {
          if (process.HeatStatusCode === StatusCodes.OPEN) {
            await changeHeatStatus(process.HeatId, StatusCodes.ARRIVE);
          } else {
            await changeHeatStatus(process.HeatId, StatusCodes.OPEN);
          }
          await refreshData();
        } else {
          if (process.LadleOpen) {
            await saveProcessData({
              processId: process.ProcessId,
              field: "LadleOpen",
              value: null,
            });
          } else {
            const now = new Date();
            await saveProcessData({
              processId: process.ProcessId,
              field: "LadleOpen",
              value: now,
            });
          }
        }
        await refreshData();
      },
      errorMessage: "Error in Open operation.",
    },
    {
      key: "close",
      label: (process) => (process.LadleClose ? "Undo Close" : "Close"),
      isDisabled: (process, disableButtons) => {
        if (disableButtons || process.IsOldProcess) {
          return true;
        }

        if (process.End !== null) {
          return true;
        }
        const canClose =
          process.LadleOpen !== null && process.LadleClose === null;
        const canUndoClose =
          process.LadleClose !== null && process.End === null;

        return !(canClose || canUndoClose);
      },
      onClick: async ({
        process,
        changeHeatStatus,
        refreshData,
        saveProcessData,
      }) => {
        if (process.HeatId !== null) {
          if (process.HeatStatusCode === StatusCodes.CLOSE) {
            await changeHeatStatus(process.HeatId, StatusCodes.OPEN);
          } else {
            await changeHeatStatus(process.HeatId, StatusCodes.CLOSE);
          }
          await refreshData();
        } else {
          if (process.LadleClose) {
            await saveProcessData({
              processId: process.ProcessId,
              field: "LadleClose",
              value: null,
            });
          } else {
            const now = new Date();
            await saveProcessData({
              processId: process.ProcessId,
              field: "LadleClose",
              value: now,
            });
          }
        }
        await refreshData();
      },
      errorMessage: "Error in Close operation.",
    },
    {
      key: "end",
      label: () => "End",
      isDisabled: (process, disableButtons) => {
        if (disableButtons || process.IsOldProcess) {
          return true;
        }

        const canEnd = process.LadleOpen !== null && process.End === null;

        return !canEnd;
      },
      onClick: async ({
        process,
        changeHeatStatus,
        setIsLadleOpen,
        setIsLadleClose,
        refreshData,
        saveProcessData,
        saveProcess,
      }) => {
        if (process.HeatId !== null) {
          await changeHeatStatus(process.HeatId, StatusCodes.END);
          setIsLadleOpen(true);
          setIsLadleClose(true);
          await refreshData();
        } else {
          const now = new Date();
          if (!process.LadleClose) {
            await saveProcessData({
              processId: process.ProcessId,
              field: "LadleClose",
              value: now,
            });
          }
          await saveProcess({
            processId: process.ProcessId,
            field: "End",
            value: now,
          });
        }
        setIsLadleOpen(true);
        setIsLadleClose(true);
        await refreshData();
      },
      errorMessage: "Error in End operation.",
    },
  ];

  return (
    <Grid item xs={12} md={1} sx={{ mt: 0.5 }}>
      <Grid container direction="column" spacing={2}>
        {buttons.map((button) => (
          <Grid item xs={12} key={button.key} sx={{ mt: 0.5 }}>
            <CustomButton
              disabled={button.isDisabled(process, disableButtons)}
              onClick={() =>
                handleButtonAction(
                  () => button.onClick(props),
                  button.errorMessage,
                )
              }
              label={button.label(process)}
            />
          </Grid>
        ))}
      </Grid>
    </Grid>
  );
};
